import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import socketService from '../services/socket';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const queryClient = useQueryClient();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const tokenRef = useRef(null);

  // Track if listeners are registered
  const listenersRegistered = useRef(false);

  // Track current effect instance to prevent stale async calls from registering listeners
  const effectIdRef = useRef(0);

  // Connect to socket when Better Auth is signed in
  useEffect(() => {
    if (!isLoaded) return;

    // Increment effect ID to invalidate any pending async calls from previous effects
    const currentEffectId = ++effectIdRef.current;

    // Define handlers
    const handleConnect = () => {
      console.log('✅ Socket connected (handler)');
      setIsConnected(true);
      setConnectionError(null);
    };

    const handleDisconnect = ({ reason }) => {
      console.log('❌ Socket disconnected:', reason);
      setIsConnected(false);
      if (reason === 'io server disconnect') {
        setConnectionError('Server disconnected');
      }
    };

    const handleError = ({ error, attempts }) => {
      console.error('Socket error:', error);
      setConnectionError(error);
      if (attempts >= 5) {
        toast.error('Connection issues. Retrying...');
      }
    };

    const handleReconnected = ({ attemptNumber }) => {
      console.log('🔄 Socket reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setConnectionError(null);
      toast.success('Reconnected to server');
      queryClient.invalidateQueries();
    };

    const handleReconnectFailed = () => {
      console.error('❌ Socket reconnection failed');
      setIsConnected(false);
      setConnectionError('Connection failed');
      toast.error('Could not connect to server. Please check your internet connection.');
    };

    const registerListeners = () => {
      // Check if this is still the current effect (prevents stale async calls from registering)
      if (currentEffectId !== effectIdRef.current) {
        console.log('🔒 Skipping listener registration - effect was superseded');
        return;
      }

      if (listenersRegistered.current) return;
      listenersRegistered.current = true;

      console.log('📡 Registering socket listeners (effect #' + currentEffectId + ')');

      // Connection status listeners
      socketService.on('socket:connected', handleConnect);
      socketService.on('socket:disconnected', handleDisconnect);
      socketService.on('socket:error', handleError);
      socketService.on('socket:reconnected', handleReconnected);
      socketService.on('socket:reconnect_failed', handleReconnectFailed);

      // Real-time event listeners
      socketService.on('schedule:created', handleScheduleCreated);
      socketService.on('schedule:updated', handleScheduleUpdated);
      socketService.on('schedule:deleted', handleScheduleDeleted);
      socketService.on('schedules:bulk_created', handleSchedulesBulkCreated);
      socketService.on('schedules:series_deleted', handleSeriesDeleted);
      socketService.on('schedules:series_updated', handleSeriesUpdated);
      socketService.on('booking:created', handleBookingCreated);
      socketService.on('booking:cancelled', handleBookingCancelled);
      socketService.on('bookings:cancelled', handleBookingsCancelled);
      socketService.on('customer:created', handleCustomerCreated);
      socketService.on('customer:updated', handleCustomerUpdated);
      socketService.on('customer:deleted', handleCustomerDeleted);
      socketService.on('video:synced', handleVideoSynced);
      socketService.on('video:updated', handleVideoUpdated);
      socketService.on('videos:synced', handleVideosSynced);
      socketService.on('settings:updated', handleSettingsUpdated);
    };

    const connectSocket = async () => {
      if (!isSignedIn) {
        console.log('Not signed in - skipping WebSocket connection');
        if (socketService.isConnected()) {
          socketService.disconnect();
          setIsConnected(false);
        }
        return;
      }

      try {
        const token = await getToken();

        // Check if effect was superseded while awaiting token
        if (currentEffectId !== effectIdRef.current) {
          console.log('🔒 Effect superseded while getting token - aborting');
          return;
        }

        if (!token) {
          console.log('No token available - skipping WebSocket connection');
          return;
        }

        tokenRef.current = token;

        // Set token getter for reconnection (so socket can get fresh tokens)
        socketService.setTokenGetter(() => getToken());

        // Connect to WebSocket if not already connected
        if (!socketService.isConnected()) {
          console.log('🔌 Connecting to WebSocket with Better Auth token...');
          socketService.connect(token);
        }

        // Register listeners AFTER socket is initialized
        registerListeners();

        // Check if already connected and update state
        if (socketService.isConnected()) {
          console.log('✅ Socket already connected, updating state');
          setIsConnected(true);
          setConnectionError(null);
        }
      } catch (error) {
        console.error('Error getting token for socket:', error);
      }
    };

    // Define real-time event handlers (will be registered in registerListeners)
    const handleScheduleCreated = ({ schedule, studioId }) => {
      console.log('📅 Schedule created:', schedule.id);

      // Add schedule to cache or replace optimistic (no invalidate = no flicker)
      queryClient.setQueriesData({ predicate: (query) => query.queryKey[0] === 'schedules' }, (oldData) => {
        if (!oldData?.data?.schedules) return oldData;

        // Check if schedule already exists (exact ID or optimistic)
        const scheduleExists = oldData.data.schedules.some(s => {
          // Check by exact ID
          if (s.id === schedule.id) return true;

          // Check by date+time+videoId for optimistic schedules
          const sTime = s.startTime?.slice(0, 5);
          const scheduleTime = schedule.startTime?.slice(0, 5);

          // For live classes, both videoId values will be null/undefined
          // Use falsy check to handle undefined vs null mismatch
          const videoIdMatches = (!s.videoId && !schedule.videoId) || s.videoId === schedule.videoId;

          return videoIdMatches &&
                 s.specificDate === schedule.specificDate &&
                 sTime === scheduleTime;
        });

        if (scheduleExists) {
          console.log('Replacing optimistic schedule with real schedule');
          // Replace optimistic or matching schedule with real one
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s => {
                // Replace optimistic schedules
                if (s._optimistic || String(s.id).startsWith('temp-')) {
                  return schedule;
                }
                // Replace by content match
                const sTime = s.startTime?.slice(0, 5);
                const scheduleTime = schedule.startTime?.slice(0, 5);
                const videoIdMatches = (!s.videoId && !schedule.videoId) || s.videoId === schedule.videoId;
                if (videoIdMatches &&
                    s.specificDate === schedule.specificDate &&
                    sTime === scheduleTime) {
                  return schedule;
                }
                return s;
              })
            }
          };
        }

        // New schedule from another client/portal - add it
        console.log('Adding new schedule from Socket.IO');
        return {
          ...oldData,
          data: {
            ...oldData.data,
            schedules: [...oldData.data.schedules, schedule]
          }
        };
      });

      // Invalidate schedules for dashboard and other views
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-schedules-countdown'] });
      queryClient.invalidateQueries({ queryKey: ['schedules-today'] });

      // Don't show toast - mutation already showed it
    };

    const handleScheduleUpdated = ({ schedule, studioId }) => {
      console.log('📅 Schedule updated:', schedule.video?.title || schedule.liveTitle || schedule.id?.substring(0, 8));

      // INSTANT: Update schedule in cache
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s =>
                s.id === schedule.id ? { ...s, ...schedule } : s
              )
            }
          };
        }
      );

      // FALLBACK: Invalidate ALL schedule queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-schedules-countdown'] });
      queryClient.invalidateQueries({ queryKey: ['schedules-today'] });

      toast(`Class updated: ${schedule.video?.title || schedule.liveTitle || 'Class'}`);
    };

    const handleScheduleDeleted = ({ id, studioId }) => {
      console.log('📅 Schedule deleted:', id);

      // INSTANT: Remove schedule from cache
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.filter(s => s.id !== id)
            }
          };
        }
      );

      // Invalidate schedules for dashboard and other views
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-schedules-countdown'] });
      queryClient.invalidateQueries({ queryKey: ['schedules-today'] });

      toast('Class cancelled');
    };

    const handleBookingCreated = ({ booking, scheduleId, studioId }) => {
      console.log('🎫 Booking created:', booking.id, 'scheduleId:', scheduleId);

      // INSTANT: Update booking count in ALL schedule queries
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s => {
                if (s.id === scheduleId) {
                  // Update current_bookings (what SchedulePage displays)
                  return { ...s, current_bookings: (s.current_bookings || 0) + 1 };
                }
                return s;
              })
            }
          };
        }
      );

      // FALLBACK: Invalidate bookings list for eventual consistency
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['class-bookings'] });

      toast.success(`New booking: ${booking.student?.firstName} ${booking.student?.lastName}`);
    };

    const handleBookingCancelled = ({ id, scheduleId, studioId }) => {
      console.log('🎫 Booking cancelled:', id, 'scheduleId:', scheduleId);

      // INSTANT: Update booking count
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s => {
                if (s.id === scheduleId) {
                  // Update current_bookings (what SchedulePage displays)
                  return { ...s, current_bookings: Math.max((s.current_bookings || 0) - 1, 0) };
                }
                return s;
              })
            }
          };
        }
      );

      // FALLBACK: Invalidate bookings list for eventual consistency
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['class-bookings'] });

      toast('Booking cancelled');
    };

    // Handle bulk booking cancellations (from schedule edit/delete)
    const handleBookingsCancelled = ({ scheduleId, scheduleIds, cancelledCount, bookingIds }) => {
      console.log('🎫 Bookings cancelled:', cancelledCount, 'for schedule', scheduleId?.substring(0, 8) || scheduleIds?.length + ' schedules');

      // Collect all affected schedule IDs
      const affectedIds = new Set();
      if (scheduleId) affectedIds.add(scheduleId);
      if (scheduleIds) scheduleIds.forEach(id => affectedIds.add(id));

      // INSTANT: Update booking counts for affected schedules
      queryClient.setQueriesData(
        { predicate: (query) => query.queryKey[0] === 'schedules' },
        (oldData) => {
          if (!oldData?.data?.schedules) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              schedules: oldData.data.schedules.map(s => {
                if (affectedIds.has(s.id)) {
                  return { ...s, current_bookings: 0 };
                }
                return s;
              })
            }
          };
        }
      );

      // Force immediate refetch to guarantee UI update
      queryClient.refetchQueries({ queryKey: ['schedules'] });
      queryClient.refetchQueries({ queryKey: ['bookings'] });
      queryClient.refetchQueries({ queryKey: ['dashboard-bookings'] });
      queryClient.refetchQueries({ queryKey: ['class-bookings'] });
    };

    const handleCustomerCreated = ({ customer, studioId }) => {
      console.log('👤 Customer created:', customer.firstName, customer.lastName);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-revenue'] });
      toast.success(`New member: ${customer.firstName} ${customer.lastName}`);
    };

    const handleCustomerUpdated = ({ customer, studioId }) => {
      console.log('👤 Customer updated:', customer.id);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-revenue'] });
    };

    const handleCustomerDeleted = ({ customerId, studioId }) => {
      console.log('👤 Customer deleted:', customerId);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      queryClient.invalidateQueries({ queryKey: ['monthly-revenue'] });
    };

    const handleVideoSynced = ({ video }) => {
      console.log('🎥 Video synced:', video.id);
      // Force refetch all video queries immediately
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'videos' });
      queryClient.refetchQueries({ predicate: (query) => query.queryKey[0] === 'videos' });
      // Don't show toast for video sync - happens frequently
    };

    const handleVideoUpdated = ({ video }) => {
      console.log('🎥 Video updated:', video.id, video.title);
      // Force refetch all video queries immediately
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'videos' });
      queryClient.refetchQueries({ predicate: (query) => query.queryKey[0] === 'videos' });
      // Don't show toast - the editing portal already shows feedback
    };

    const handleVideosSynced = ({ synced, deleted, results }) => {
      console.log(`🎥 Bulk video sync: ${synced} synced, ${deleted} deleted`);
      // Force refetch all video queries immediately
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'videos' });
      queryClient.refetchQueries({ predicate: (query) => query.queryKey[0] === 'videos' });
      // Don't show toast for bulk sync - happens on startup
    };

    const handleSettingsUpdated = ({ settings, studioId }) => {
      console.log('⚙️ Settings updated via WebSocket:', { studioId });

      // Update localStorage (handle case where 'studio' might be "undefined" string)
      const storedStudioStr = localStorage.getItem('studio');
      const storedStudio = (storedStudioStr && storedStudioStr !== 'undefined')
        ? JSON.parse(storedStudioStr)
        : {};
      localStorage.setItem('studio', JSON.stringify({ ...storedStudio, settings }));

      // Dispatch event for AuthContext to update
      window.dispatchEvent(new CustomEvent('settings:updated', { detail: { settings } }));
      // Don't show toast - the settings page mutation already shows it
    };

    const handleSchedulesBulkCreated = ({ seriesId, instanceCount, studioId }) => {
      console.log(`📅 Bulk schedules created: ${instanceCount} instances for series ${seriesId}`);
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'schedules' || key === 'dashboard-schedules' || key === 'upcoming-schedules-countdown' || key === 'schedules-today';
        }
      });
      // Don't show toast - the schedule page mutation already shows it
    };

    const handleSeriesDeleted = ({ seriesId, instanceCount, studioId }) => {
      console.log(`📅 Recurring series deleted: ${seriesId} (${instanceCount} instances)`);
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'schedules' || key === 'dashboard-schedules' || key === 'upcoming-schedules-countdown' || key === 'schedules-today';
        }
      });
      // Don't show toast - the schedule page mutation already shows it
    };

    const handleSeriesUpdated = ({ seriesId, instanceCount, studioId, partial }) => {
      console.log(`📅 Recurring series updated: ${seriesId} (${instanceCount} instances${partial ? ', partial update' : ''})`);
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'schedules' || key === 'dashboard-schedules' || key === 'upcoming-schedules-countdown' || key === 'schedules-today';
        }
      });
      // Don't show toast - the schedule page mutation already shows it
    };

    // Now call connectSocket which will call registerListeners after socket is ready
    connectSocket();

    // Cleanup on unmount - remove listeners but keep socket connected
    return () => {
      console.log('🧹 Cleaning up socket listeners (effect #' + currentEffectId + ')');

      socketService.off('socket:connected', handleConnect);
      socketService.off('socket:disconnected', handleDisconnect);
      socketService.off('socket:error', handleError);
      socketService.off('socket:reconnected', handleReconnected);
      socketService.off('socket:reconnect_failed', handleReconnectFailed);

      socketService.off('schedule:created', handleScheduleCreated);
      socketService.off('schedule:updated', handleScheduleUpdated);
      socketService.off('schedule:deleted', handleScheduleDeleted);
      socketService.off('schedules:bulk_created', handleSchedulesBulkCreated);
      socketService.off('schedules:series_deleted', handleSeriesDeleted);
      socketService.off('schedules:series_updated', handleSeriesUpdated);
      socketService.off('booking:created', handleBookingCreated);
      socketService.off('booking:cancelled', handleBookingCancelled);
      socketService.off('bookings:cancelled', handleBookingsCancelled);
      socketService.off('customer:created', handleCustomerCreated);
      socketService.off('customer:updated', handleCustomerUpdated);
      socketService.off('customer:deleted', handleCustomerDeleted);
      socketService.off('video:synced', handleVideoSynced);
      socketService.off('video:updated', handleVideoUpdated);
      socketService.off('videos:synced', handleVideosSynced);
      socketService.off('settings:updated', handleSettingsUpdated);

      // Reset listeners flag so they can be re-registered on next mount
      listenersRegistered.current = false;

      // Don't disconnect - let the socket persist for the session
      // Only disconnect when signed out (handled in the connectSocket check above)
    };
  }, [queryClient, isLoaded, isSignedIn, getToken]);

  const value = {
    socket: socketService,
    isConnected,
    connectionError
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
